import { useEffect, useRef, useCallback, useMemo } from 'react';

import config from '../config';
import { getCode } from '../middleware/activate';

/** Channel type screen pop. */
export const CHANNEL_SCREEN_POP = 'screen_pop';

/** Channel type screen pop started. */
export const CHANNEL_SCREEN_POP_STARTED = `${CHANNEL_SCREEN_POP}_started`;

/** Channel type screen pop ended. */
export const CHANNEL_SCREEN_POP_ENDED = `${CHANNEL_SCREEN_POP}_ended`;

export function useSharedPusher(user) {
  const workerRef = useRef(null);
  const listenersRef = useRef(new Map());

  const hasDialpadUserId = useMemo(() => !!user?.get('dialpadUserId'), [user]);

  useEffect(() => {
    if (!hasDialpadUserId) return;

    initWorker();

    return () => {
      workerRef.current?.port.close();
      listenersRef.current.clear();
    };
  }, [hasDialpadUserId]);

  const initWorker = () => {
    const token = getCode();
    const worker = new SharedWorker('/js/shared-pusher-worker.js');

    workerRef.current = worker;
    worker.port.start();

    worker.port.postMessage({
      type: 'init',
      payload: {
        key: config.PUSHER_KEY,
        cluster: config.PUSHER_CLUSTER,
        authEndpoint: `${config.API_BASE_URL}/api/v1/broadcasting/auth`,
        token,
      },
    });

    worker.port.onmessage = messageEvent => {
      const { type, payload } = messageEvent.data;

      if (type === 'reinitialize') {
        refreshToken();
      }

      if (type === 'event') {
        const { channel, event, data } = payload;
        const key = `${channel}:${event}`;
        const listener = listenersRef.current.get(key);

        if (listener) listener(data);
      }
    };
  };

  const refreshToken = async () => {
    const newToken = await getCode();

    workerRef.current?.port.postMessage({
      type: 'updateToken',
      payload: { token: newToken },
    });
  };

  const subscribe = useCallback(
    (channelName, eventName, callback) => {
      if (!hasDialpadUserId || !workerRef.current) return;

      const key = `${channelName}:${eventName}`;

      listenersRef.current.set(key, callback);

      workerRef.current.port.postMessage({
        type: 'subscribe',
        payload: { channelName },
      });
    },
    [hasDialpadUserId],
  );

  const unsubscribe = useCallback(
    (channelName, eventName) => {
      if (!hasDialpadUserId) return;

      const key = `${channelName}:${eventName}`;

      listenersRef.current.delete(key);

      workerRef.current?.port.postMessage({
        type: 'unsubscribe',
        payload: { channelName },
      });
    },
    [hasDialpadUserId],
  );

  const simulate401 = useCallback(channelName => {
    workerRef.current?.port.postMessage({
      type: 'simulate401',
      payload: { channelName },
    });
  }, []);

  return { subscribe, unsubscribe, refreshToken, simulate401 };
}
